import Route from '@ember/routing/route';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { getLocalDateInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { MAX_DAYS_IN_FUTURE } from 'lumen/utils/constants/market-insight-constants';
import {
  DEFAULT_DAYS_SHOWN_IN_OD,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import OdApiResponse from 'lumen/utils/datamodels/overview-dashboard/od-api-response';
import { getDateAggregation } from 'lumen/utils/overview-dashboard/daily-patterns-utils';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';

export default class HotelOverviewRoute extends Route {
  queryParams = {
    fromDate: {
      refreshModel: true,
    },
    toDate: {
      refreshModel: true,
    },
    refDaysAgo: {
      refreshModel: false,
    },
    activeFilters: {
      refreshModel: false,
    },
    otaId: {
      refreshModel: true,
    },
    compsetId: {
      refreshModel: true,
    },
    rateParam: {
      refreshModel: true,
    },
  };

  @service access;
  @service ajax;
  @service drawer;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service miSelections;
  @service odAccess;
  @service odSelections;
  @service transition;

  dailyPatternsOtbDataRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
        };

        const compareDatesParams = this.generateCompareDatesUrlParameters();

        // The endpoint expects dateAggregation as query param
        // When the selected range is 30 days or less, we send DATE_AGGREGATION.DAY and the data returned from the API is per day
        // When the selected date picker range is greater than 30 days, we send DATE_AGGREGATION.WEEK and the data returned from the API is aggregated per week
        const dateAggregation = getDateAggregation(rangeStart, rangeEnd);

        const requestUrl = formatUrl(
          '/apigateway/v1/app/revenueinsight/occupancies/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
            date_aggregation: dateAggregation,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }

      return null;
    },
  );

  dailyPatternsPricePositionDataRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd, otaId, compsetId, rateParam) => {
      let apiResponse = null;

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: this.hotelAndMarketService.hotel.id,
          ota: otaId,
          compset_ids: compsetId,
          los: this.defaultLosRates,
          persons: this.defaultPersons,
        };
        mapRateParamToAPIParams(rateParam, standardParams);

        // The endpoint expects dateAggregation as query param
        // When the selected range is 30 days or less, we send DATE_AGGREGATION.DAY and the data returned from the API is per day
        // When the selected date picker range is greater than 30 days, we send DATE_AGGREGATION.WEEK and the data returned from the API is aggregated per week
        const dateAggregation = getDateAggregation(rangeStart, rangeEnd);

        const requestUrl = formatUrl(
          '/apigateway/v1/app/rates/pricing_position/kpis',
          {
            ...standardParams,
            date_aggregation: dateAggregation,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }

      return null;
    },
  );

  eventsDataRequestTaskResultTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      // This means that the daterange isn't valid.
      // In theory this endpoint supports past data, but it wouldn't be consistent with other KPIs in this section
      // Fallback to an empty object
      if (rangeStart === null && rangeEnd === null) {
        return OdApiResponse.createEmptyResponse();
      }

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: this.hotelAndMarketService.hotel.id,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/events/kpis',
          standardParams,
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }

      return null;
    },
  );

  marketDemandRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      // This means that the daterange isn't valid, since this KPI only supports forward looking data
      // Fallback to an empty object
      if (!rangeStart && !rangeEnd) {
        return OdApiResponse.createEmptyResponse();
      }

      const todayMoment = getLocalDateInBrowserTimezone(this.timezone).format(
        'YYYY-MM-DD',
      );

      const daysBetweenTodayAndStart = Math.ceil(
        (new Date(rangeStart).getTime() - new Date(todayMoment).getTime()) /
          (1000 * 3600 * 24),
      );

      if (daysBetweenTodayAndStart > MAX_DAYS_IN_FUTURE) {
        return OdApiResponse.createEmptyResponse();
      }

      const daysBetweenTodayAndEnd = Math.ceil(
        (new Date(rangeEnd).getTime() - new Date(todayMoment).getTime()) /
          (1000 * 3600 * 24),
      );

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          // If the difference between today and range end is greater than 351 days, only MI data within the allowed range will be requested
          from_date_range_end:
            daysBetweenTodayAndEnd > MAX_DAYS_IN_FUTURE
              ? this.hotelAndMarketService
                  .getLocalTime()
                  .add(MAX_DAYS_IN_FUTURE, 'day')
                  .format('YYYY-MM-DD')
              : rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
        };

        const compareDatesParams = this.generateCompareDatesUrlParameters();

        const requestUrl = formatUrl(
          '/apigateway/v1/app/marketinsight/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }
      return null; // in this case something went wrong with the request
    },
  );

  otbDataRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      try {
        // Prepare request parameters
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
        };
        const compareDatesParams = this.generateCompareDatesUrlParameters();

        const requestUrl = formatUrl(
          '/apigateway/v1/app/revenueinsight/occupancies/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }
      return null; // in this case something went wrong with the request
    },
  );

  parityDataRequestTask = task(
    { restartable: true },
    async (otaId, compsetId, rateParam) => {
      let apiResponse = null;

      try {
        const standardParams = {
          hotel_id: this.hotelAndMarketService.hotel.id,
          los: this.defaultLosRates,
          persons: this.defaultPersons,
        };

        mapRateParamToAPIParams(rateParam, standardParams);

        const requestUrl = formatUrl(
          '/apigateway/v1/app/parities/kpis',
          standardParams,
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return apiResponse;
      }

      return null;
    },
  );

  priceDifferenceRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd, otaId, compsetId, rateParam) => {
      let apiResponse = null;

      // This means that the daterange isn't valid, since this KPI only supports forward looking data
      // Fallback to an empty object
      if (!rangeStart && !rangeEnd) {
        return OdApiResponse.createEmptyResponse();
      }

      try {
        // Prepare queryparams for the Price difference request (makes use of filters in the overview navbar)
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          hotel_id: this.hotelAndMarketService.hotel.id,
          ota: otaId,
          compset_ids: compsetId,
          los: this.defaultLosRates,
          persons: this.defaultPersons,
        };
        mapRateParamToAPIParams(rateParam, standardParams);

        const compareDatesParams = this.generateCompareDatesUrlParameters();

        const requestUrl = formatUrl(
          '/apigateway/v1/app/rates/competitorrates/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }

      return null;
    },
  );

  rankingDataRequestTask = task({ restartable: true }, async (compsetId) => {
    let apiResponse = null;

    try {
      const standardParams = {
        hotel_id: this.hotelAndMarketService.hotel.id,
        los: this.hotelAndMarketService.hotel.defaultLosRates[0],
        compset_ids: compsetId,
      };

      const requestUrl = formatUrl(
        '/apigateway/v1/app/rankings/kpis',
        standardParams,
        this.ajax.host,
      );

      apiResponse = await this.ajax.request(requestUrl);
    } catch (error) {
      this.basicErrorHandling(error);
    }

    if (apiResponse) {
      return apiResponse;
    }

    return null;
  });

  rentalDataRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      try {
        // Prepare request parameters
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
        };
        const compareDatesParams = this.generateCompareDatesUrlParameters();

        const requestUrl = formatUrl(
          '/apigateway/v1/app/shorttermrental/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
            hotel_id: this.hotelAndMarketService.hotel.id,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }
      return null; // in this case something went wrong with the request
    },
  );

  requestAlertsDataTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd, otaId, compsetId, rateParam, refDaysAgo) => {
      let apiResponse = null;

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
          compare_extract_date:
            this.odSelections.refDaysAgoDatesInAPIFormat[refDaysAgo],
          ota: otaId,
          compset_ids: compsetId,
          los: this.defaultLosRates,
          persons: this.defaultPersons,
        };

        mapRateParamToAPIParams(rateParam, standardParams);

        const requestUrl = formatUrl(
          '/api/v3/overview/alerts/',
          standardParams,
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return apiResponse;
    },
  );

  revenueDataRequestTask = task(
    { restartable: true },
    async (rangeStart, rangeEnd) => {
      let apiResponse = null;

      try {
        const standardParams = {
          from_date_range_start: rangeStart,
          from_date_range_end: rangeEnd,
          subscription_id: this.hotelAndMarketService.hotel.id,
        };

        const compareDatesParams = this.generateCompareDatesUrlParameters();

        const requestUrl = formatUrl(
          '/apigateway/v1/app/revenueinsight/revenue/kpis',
          {
            ...standardParams,
            ...compareDatesParams,
            convert_currency: true,
          },
          this.ajax.host,
        );

        apiResponse = await this.ajax.request(requestUrl);
      } catch (error) {
        this.basicErrorHandling(error);
      }

      if (apiResponse) {
        return OdApiResponse.fromApiResponse(apiResponse);
      }
      return null; // in this case something went wrong with the request
    },
  );

  get defaultLosRates() {
    return this.hotelAndMarketService.hotelOrMarket?.defaultLosRates?.[0];
  }

  get defaultPersons() {
    return this.hotelAndMarketService.hotelOrMarket?.defaultPersons;
  }

  get timezone() {
    return this.hotelAndMarketService.timezone;
  }

  basicErrorHandling(error) {
    if (error) {
      this.errorTracking.captureException(error);
    }

    // Note: commented this out for now. In Overview we maybe don't want to show error dialogs all over the place (e.g. 1 product can have issues, but rest of dashboard can still be useful)
    // this.alerting.showErrorAlert({ text: getAPIErrorMessage(error, this.intl) });
  }

  beforeModel(transition) {
    if (!this.access.hasOverviewDashboardTab) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateOverviewDashboardQueryParams(transition);

      // calculate and set the correct reference date values in the ED Selections service, because we'll need these a lot for the API requests
      this.odSelections.setRefDaysAgoDatesInAPIFormat();
    }
  }

  // For several of the new CA endpoints we need to pass all desired compare dates to get the data for our standard ref periods (vs 1 - 3 - 7 - 14 days)
  clearCachedData() {
    this.odSelections.clearValues();
  }

  // This function generates these parameters, based on a prepared object in the caSelections service
  generateCompareDatesUrlParameters() {
    const requestCompareDatesUrlParameters = {};

    Object.keys(this.odSelections.refDaysAgoDatesInAPIFormat).forEach(
      (key, index) => {
        if (index > 0) {
          requestCompareDatesUrlParameters[`compare${index + 1}_extract_date`] =
            this.odSelections.refDaysAgoDatesInAPIFormat[key];
        } else {
          requestCompareDatesUrlParameters.compare_extract_date =
            this.odSelections.refDaysAgoDatesInAPIFormat[key];
        }
      },
    );

    return requestCompareDatesUrlParameters;
  }

  model(params) {
    const { fromDate, toDate, otaId, compsetId, rateParam, refDaysAgo } =
      params;

    let rangeStart = null;
    let rangeEnd = null;

    if (fromDate) {
      rangeStart = fromDate;
    } else {
      rangeStart = this.hotelAndMarketService
        .getLocalTime()
        .format('YYYY-MM-DD');
    }

    if (toDate) {
      rangeEnd = toDate;
    } else {
      rangeEnd = this.hotelAndMarketService
        .getLocalTime()
        .add(DEFAULT_DAYS_SHOWN_IN_OD, 'day')
        .format('YYYY-MM-DD');
    }

    // Make sure odSelections service has the correct daterange used in the datepicker of the page
    this.odSelections.datepickerFromDate = rangeStart;
    this.odSelections.datepickerToDate = rangeEnd;

    // Specific daterange values that should be used for all requests for kpis that only support showing future data
    // E.g.: Avg. Market demand, Pricing difference,...
    const rangeStartForKpisWithForwardLookingData =
      this.odSelections.dateRangeForKpisWithForwardLookingData.start;
    const rangeEndForKpisWithForwardLookingData =
      this.odSelections.dateRangeForKpisWithForwardLookingData.end;

    const model = {};

    // Place every request to a separate API in it's own task
    // For now we always request the range selected in the top datepicker
    // -> if a day is selected for day drilldown, it will always be in the range of the datepicker. We'll make use of the same data and filter out the correct day on FE side
    // Get the data that can be used for My OTB, Market OTB, My pickup & Market pickup KPIs
    if (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb) ||
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.market_otb)
    ) {
      const otbDataRequestTaskResult = this.otbDataRequestTask.perform(
        rangeStart,
        rangeEnd,
      );
      model.otbDataRequestTask = otbDataRequestTaskResult;
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)) {
      const rentalDataRequestTaskResult = this.rentalDataRequestTask.perform(
        rangeStart,
        rangeEnd,
      );
      model.rentalDataRequestTask = rentalDataRequestTaskResult;
    }

    if (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_revenue) ||
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_adr)
    ) {
      const revenueDataRequestTaskResult = this.revenueDataRequestTask.perform(
        rangeStart,
        rangeEnd,
      );
      model.revenueDataRequestTask = revenueDataRequestTaskResult;
    }

    if (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.demand_level) ||
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.top_searching_countries)
    ) {
      let rangeEndForForwardLookingDataPlusMICheck =
        rangeEndForKpisWithForwardLookingData;

      // Extra specific MI datarange check (if we request the full last possible month in the datepicker we might go over the max allowed MI date)
      // This would result in a 400 response. Currently this is not reflected to the user in the UI
      const { maxAllowedDay } = this.miSelections;

      if (
        maxAllowedDay &&
        dayjs(rangeEndForForwardLookingDataPlusMICheck).isAfter(
          maxAllowedDay,
          'day',
        )
      ) {
        rangeEndForForwardLookingDataPlusMICheck =
          maxAllowedDay.format('YYYY-MM-DD');
      }
      model.marketDemandDataRequestTask = this.marketDemandRequestTask;

      model.marketDemandDataRequestTask.perform(
        rangeStartForKpisWithForwardLookingData,
        rangeEndForForwardLookingDataPlusMICheck,
      );
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.pricing_difference)) {
      const priceDifferenceDataRequestTask =
        this.priceDifferenceRequestTask.perform(
          rangeStartForKpisWithForwardLookingData,
          rangeEndForKpisWithForwardLookingData,
          otaId,
          compsetId,
          rateParam,
        );
      model.priceDifferenceDataRequestTask = priceDifferenceDataRequestTask;
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.stay_date_graph)) {
      if (
        this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb) ||
        this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.market_otb)
      ) {
        const dailyPatternsOtbDataRequestTaskResult =
          this.dailyPatternsOtbDataRequestTask.perform(rangeStart, rangeEnd);
        model.dailyPatternsOtbDataRequestTask =
          dailyPatternsOtbDataRequestTaskResult;
      }

      if (
        this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.pricing_difference)
      ) {
        const dailyPatternsPricePositionDataRequestTaskResult =
          this.dailyPatternsPricePositionDataRequestTask.perform(
            rangeStart,
            rangeEnd,
            otaId,
            compsetId,
            rateParam,
          );
        model.dailyPatternsPricePositionDataRequestTask =
          dailyPatternsPricePositionDataRequestTaskResult;
      }
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.events)) {
      const eventsDataRequestTaskResult =
        this.eventsDataRequestTaskResultTask.perform(
          rangeStartForKpisWithForwardLookingData,
          rangeEndForKpisWithForwardLookingData,
        );
      model.eventsDataRequestTask = eventsDataRequestTaskResult;
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.ranking)) {
      model.rankingDataRequestTask =
        this.rankingDataRequestTask.perform(compsetId);
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_parity)) {
      model.parityDataRequestTask = this.parityDataRequestTask.perform(
        otaId,
        compsetId,
        rateParam,
      );
    }

    // Next to all tile requests, we also need to request the alerts (for the My highlights feed)
    // Here we pass the task down, so that we can relaunch the alerts request if needed
    model.requestAlertsDataTask = this.requestAlertsDataTask;

    // Initial perform so that we fetch the alerts on page load
    model.requestAlertsDataTask.perform(
      rangeStart,
      rangeEnd,
      otaId,
      compsetId,
      rateParam,
      refDaysAgo,
    );

    return model;
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      this.clearCachedData();
      controller.activeFilters = '';
    }
  }
}
